/* eslint-disable max-lines */
import axios from 'axios';
import { GetMailMonitor, GetMailDocumentHistory, CompanyMovType, GetAssetHistory } from 'models/response';
import {
  GetDiskMonitorResponse,
  GetFileUploadMonitorResponse,
  DocumentHistoryResponse,
  Error,
  Job,
  PendingResponse,
} from 'models/response';
import { GetDiskMonitor, GetDocumentFromDataRepository, GetDocumentsFromDataRepositoryDynamic } from 'models/request';
import { trackPromise } from 'react-promise-tracker';
import qs from 'query-string';

import { MONITORSERVICE } from 'utils/constants';
import { Row } from 'models/table';

/**
 * Gets the data for the mail monitor mail table
 * @param {string} dateFrom
 * @param {string} dateTo
 * @return {Promise<AxiosResponse<GetMailMonitor[]>>}
 */
const getMailMonitor = (dateFrom: string, dateTo: string) =>
  trackPromise(axios.post<GetMailMonitor[]>(`${MONITORSERVICE}/getMailMonitor`, { dateFrom, dateTo }), 'module');

/**
 * Gets the history data for the monitor mail
 * @param {number} protocol
 * @return {Promise<AxiosResponse<GetMailDocumentHistory[]>>}
 */
const getMailDocumentHistory = (protocol: number) =>
  trackPromise(
    axios.get<GetMailDocumentHistory[]>(`${MONITORSERVICE}/getMailDocumentHistory`, {
      params: { protocol },
    }),
    'modal',
  );

/**
 * This API gets disk monitor data
 * @param {Object} body
 * @param {string} Object.dateFrom
 * @param {string} Object.dateTo
 * @return {Promise<AxiosResponse<GetDiskMonitor[]>>}
 */
const getDiskMonitor = (body: GetDiskMonitor) =>
  trackPromise(axios.post<GetDiskMonitorResponse[]>(`${MONITORSERVICE}/getDiskMonitor`, { ...body }), 'module');

/**
 * Gets the data for the mail monitor mail table
 * @param {string} dateFrom
 * @param {string} dateTo
 * @return {Promise<AxiosResponse<GetFileUploadMonitorResponse[]>>}
 */
const getFileUploadMonitor = (dateFrom: string, dateTo: string) =>
  trackPromise(
    axios.post<GetFileUploadMonitorResponse[]>(`${MONITORSERVICE}/getFileUploadMonitor`, { dateFrom, dateTo }),
    'module',
  );

/**
 * This API gets document history
 * @param {number} protocol
 * @return {Promise<AxiosResponse<DocumentHistoryResponse[]>>}
 */
const getDocumentHistory = (protocol: number) =>
  trackPromise(
    axios.get<DocumentHistoryResponse[]>(`${MONITORSERVICE}/getDocumentHistory`, {
      params: { protocol },
    }),
    'modal',
  );

/**
 * Gets errors
 * @param {number | null} idCompany
 * @return {Promise<AxiosResponse<Error[]>>}
 */
const getErrors = (idCompany: number | null) =>
  trackPromise(
    axios.get<Error[]>(`${MONITORSERVICE}/getErrors`, {
      params: { idCompany },
    }),
    'module',
  );

/**
 * Retry errors
 * @param {number[]} protocols
 * @return {Promise<AxiosResponse>}
 */
const retryErrors = (protocols: number[]) => axios.post(`${MONITORSERVICE}/retryErrors`, { protocols });

/**
 * Delete errors
 * @param {number[]} protocols
 * @return {Promise<AxiosResponse>}
 */
const deleteErrors = (protocols: number[]) =>
  trackPromise(axios.post(`${MONITORSERVICE}/deleteErrors`, { protocols }), 'module');

/**
 * Gets list of jobs
 * @return {Promise<AxiosResponse<Job[]>>}
 */
const getListOfJobs = () => trackPromise(axios.get<Job[]>(`${MONITORSERVICE}/getListOfJobs`), 'module');

/**
 * Run job
 * @param {string} jobName
 * @return {Promise<AxiosResponse<Job>>}
 */
const runJob = (jobName: string) =>
  trackPromise(axios.post(`${MONITORSERVICE}/runJob`, qs.stringify({ jobName })), 'modal');

/**
 * Resume job
 * @param {string} jobName
 * @return {Promise<AxiosResponse<Job>>}
 */
const resumeJob = (jobName: string) =>
  trackPromise(axios.post(`${MONITORSERVICE}/resumeJob`, qs.stringify({ jobName })), 'modal');

/**
 * Pause job
 * @param {string} jobName
 * @return {Promise<AxiosResponse<Job>>}
 */
const pauseJob = (jobName: string) =>
  trackPromise(axios.post(`${MONITORSERVICE}/pauseJob`, qs.stringify({ jobName })), 'modal');

/**
 * Get mail download
 * @param {number} idProtocol
 * @param {number} idChannel
 * @return {Promise<AxiosResponse<string>>}*/
const downloadMail = (idProtocol: number, idChannel: number) =>
  trackPromise(
    axios.get<string>(`${MONITORSERVICE}/downloadMail`, {
      params: { idProtocol, idChannel },
    }),
    'module',
  );

/**
 * This API gets pending documents
 * @param {Object} body
 * @param {number | null} idCompany
 * @return {Promise<AxiosResponse<PendingResponse[]>>}
 */
const getPending = (body: { idCompany: number | null }) =>
  trackPromise(axios.post<PendingResponse[]>(`${MONITORSERVICE}/getPending`, { ...body }), 'module');

/**
 * API called for retry action pending documents
 * @param {Object} body
 * @param {number[]} protocols
 * @return {Promise<AxiosResponse>}
 */
const retryPending = (body: { protocols: number[] }) =>
  trackPromise(axios.post(`${MONITORSERVICE}/retryPending`, { ...body }), 'module');

/**
 * API called for force retry action pending documents
 * @param {Object} body
 * @param {number[]} protocols
 * @return {Promise<AxiosResponse>}
 */
const forceRetriedDocuments = (body: { protocols: number[] }) =>
  trackPromise(axios.post(`${MONITORSERVICE}/forceRetriedDocuments`, { ...body }), 'module');

/**
 * This API returns the Pdf doc image in base64 for Monitor
 * @param {number} protocol
 * @return {Promise<AxiosResponse<string>>}
 */
const getMonitorDocumentImage = (protocol: number) => {
  return trackPromise(axios.get<string>(`${MONITORSERVICE}/getDocumentImage?protocol=${protocol || 0}`), 'pdf');
};

const getCompanyMovType = (idCompany: number) => {
  return trackPromise(
    axios.get<CompanyMovType[]>(`${MONITORSERVICE}/getCompanyMovType`, {
      params: { idCompany },
    }),
    'module',
  );
};

/**
 *
 * @param body {GetDocumentFromDataRepository}
 * @returns {Promise<AxiosResponse>}
 */

const getDocumentsFromDataRepository = (body: GetDocumentFromDataRepository) =>
  trackPromise(axios.post(`${MONITORSERVICE}/getDocumentsFromDataRepository`, body), 'module');

/**
 * This API gets e-invoice monitor data
 * @param {Object} body
 * @param {string} Object.dateFrom
 * @param {string} Object.dateTo
 * @return {Promise<AxiosResponse<GetDiskMonitor[]>>}
 */
const getEInvoiceMonitors = (body: GetDiskMonitor) =>
  trackPromise(axios.post<GetDiskMonitorResponse[]>(`${MONITORSERVICE}/getEInvoiceMonitor`, { ...body }), 'module');

const downloadArchivePDF = (protocols: number[]) => {
  return trackPromise(
    axios.post(
      `${MONITORSERVICE}/getSelectedDocumentsPDFArchives`,
      { protocols },
      {
        responseType: 'blob',
      },
    ),
    'module',
  );
};
/**
 * @param {Object} body request
 * @param {number} Object.idProgram
 * @param {number} Object.idUser
 * @param {number} Object.idTemplate
 * @param {IFilterValue[]} Object.filters
 * @return {Promise<AxiosResponse>}
 */
const getDocumentsFromDataRepositoryDynamic = (body: GetDocumentsFromDataRepositoryDynamic) =>
  trackPromise(axios.post<Row[]>(`${MONITORSERVICE}/getDocumentsFromDataRepositoryDynamic`, body), 'module');

const getAssetDocumentHistory = (idSubject: number) => {
  return trackPromise(
    axios.get<GetAssetHistory[]>(`${MONITORSERVICE}/getAssetDocumentHistory`, {
      params: { idSubject },
    }),
    'modal',
  );
};

/**
 * Get error download
 * @param {number[]} protocols
 * @return {Promise<AxiosResponse<string>>}*/
const downloadError = (protocols: number[]) =>
  trackPromise(
    axios.post(
      `${MONITORSERVICE}/getSelectedErrorDocumentsAttachments`,
      { protocols },
      {
        responseType: 'blob',
      },
    ),
    'module',
  );

export default {
  getDiskMonitor,
  getDocumentHistory,
  getMailDocumentHistory,
  getMailMonitor,
  getFileUploadMonitor,
  getErrors,
  retryErrors,
  deleteErrors,
  getListOfJobs,
  runJob,
  resumeJob,
  pauseJob,
  downloadMail,
  getPending,
  retryPending,
  forceRetriedDocuments,
  getMonitorDocumentImage,
  getCompanyMovType,
  getDocumentsFromDataRepository,
  getEInvoiceMonitors,
  downloadArchivePDF,
  getDocumentsFromDataRepositoryDynamic,
  getAssetDocumentHistory,
  downloadError,
};
